import GrabID from '@grab-id/grab-id-client';
import jwtDecode from 'jwt-decode';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import Storage from 'grab-storage';
import {
  STORAGE_GRABID_AUTH_RESPONSES,
  STORAGE_GRABID_USER_INFO,
  STORAGE_USER_COUNTRY_CODE,
  COUNTRY_LOCALE_MAP,
} from 'constants/app-constants';
import { isProduction, isDev } from 'common/utils/environment-util';

/*
 * accessToken
 * code
 * codeVerifier
 * idToken
 * nonce
 * state
*/
export const saveAuthReponses = tokenResult => {
  Storage.setItem(STORAGE_GRABID_AUTH_RESPONSES, JSON.stringify(tokenResult));
};

export const clearAuthResponses = () => {
  Storage.removeItem(STORAGE_GRABID_AUTH_RESPONSES);
  Storage.removeItem(STORAGE_GRABID_USER_INFO);
};

export const clearGrabIdTokens = () => {
  window.localStorage.removeItem('grabid:access_token');
  window.localStorage.removeItem('grabid:id_token');
  window.localStorage.removeItem('grabid:refresh_token');
};

/*
 * aud
 * sub
 * pid
 * svc
 * name
 * email
 * email_verified
 * auth_time
*/
export const savePaxUserInfo = userInfo => {
  Storage.setItem(STORAGE_GRABID_USER_INFO, JSON.stringify(userInfo));
};

export const getPaxUserInfo = () =>
  JSON.parse(Storage.getItem(STORAGE_GRABID_USER_INFO));

// Remove sensitive PII data from local storage after use
export const clearPaxPIIData = () => {
  const userInfo = getPaxUserInfo();
  delete userInfo.email;
  delete userInfo.name;
  savePaxUserInfo(userInfo);
};

export const getPaxID = () => _get(getPaxUserInfo(), 'sub');

export const fetchPaxUserInfo = async accessToken => {
  if (!accessToken) return null;

  const { openIdUrl } = getGrabIDConfigs();
  try {
    const response = await window.fetch(`${openIdUrl}/userinfo`, {
      method: 'GET',
      headers: {
        'Accept-Content': 'application/json; charset=utf-8',
        Authorization: `Bearer ${accessToken}`,
        'X-Request-ID': 'DEBUG-GE-WEB',
      },
      mode: 'cors',
    });
    return await response.json();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    return null;
  }
};

export const getAuthTokenResults = () =>
  JSON.parse(Storage.getItem(STORAGE_GRABID_AUTH_RESPONSES));

export const getPaxAccessToken = () =>
  _get(
    JSON.parse(Storage.getItem(STORAGE_GRABID_AUTH_RESPONSES)),
    'accessToken',
    null,
  );

// checkTokenExpiry = false: only check token existence
// checkTokenExpiry = true:  check token existence & not expired
export const isGrabIDAuthenticated = (checkTokenExpiry = true) => {
  const token = getPaxAccessToken();

  if (!token) return false;

  if (!checkTokenExpiry) return true;

  try {
    const decodedToken = jwtDecode(token);
    if (!decodedToken.exp || decodedToken.exp < Date.now() / 1000) {
      return false;
    }
  } catch (err) {
    const errorCtx = rumErrorCtx()
      .component('isGrabIDAuthenticated')
      .message(err.message || 'Failed to decode JWT token')
      .severity(SEVERITY.CRITICAL)
      .build();
    ddRum.addError(err, errorCtx);
    // eslint-disable-next-line no-console
    console.error(err);
    return false;
  }

  return true;
};

// To tell if current user is login via GrabID
export const isPaxUser = () => isGrabIDAuthenticated(false);

const getGrabIDConfigs = () => {
  let redirectUri;
  let clientId;
  let openIdUrl;
  let clientSecret;
  if (isDev()) {
    redirectUri = 'https://ge-web-booking.grab.dev/book/redirectGrabId';
    clientId = 'a47df87853124fb3b8d8096f1d027cf9';
    openIdUrl = GrabID.GrabPartnerUrls.STAGING;
  } else if (isProduction()) {
    redirectUri = 'https://express.grab.com/book/redirectGrabId';
    clientId = '04318e8215e44407be6ae24c4478800a';
    openIdUrl = GrabID.GrabPartnerUrls.PRODUCTION;
  } else {
    redirectUri = 'https://express.stg-myteksi.com/book/redirectGrabId';
    clientId = '293a83710927494394c66b091658e1e5';
    openIdUrl = GrabID.GrabPartnerUrls.STAGING;
  }
  return {
    redirectUri,
    clientId,
    openIdUrl,
    clientSecret,
  };
};

class TokenResponseWithRefreshToken {
  // modified from https://github.com/grab/grabplatform-sdk-js/blob/master/src/token_request_handler.ts#L65
  accessToken;
  idToken;
  tokenType;
  expiresIn;
  refreshToken;

  constructor(accessToken, idToken, tokenType, expiresIn, refreshToken) {
    this.accessToken = accessToken;
    this.idToken = idToken;
    this.tokenType = tokenType;
    this.expiresIn = expiresIn;
    this.refreshToken = refreshToken;
  }

  static fromJSON(responseJSON) {
    return new TokenResponseWithRefreshToken(
      responseJSON.access_token,
      responseJSON.id_token,
      responseJSON.token_type,
      responseJSON.expires_in,
      responseJSON.refresh_token,
    );
  }
}

const makeTokenRequestHandleRefreshToken = async grabIDClient => {
  // modified from https://github.com/grab/grabplatform-sdk-js/blob/master/src/index.ts#L183
  const codeVerifier = window.localStorage.getItem('grabid:code_verifier');
  const code = window.localStorage.getItem('grabid:code');

  if (!codeVerifier || !code) {
    throw new Error('Please get authorization code first');
  }

  const tokenRequest = {
    client_id: grabIDClient.clientId,
    code_verifier: codeVerifier,
    grant_type: 'authorization_code',
    redirect_uri: grabIDClient.redirectUri,
    code,
  };

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify(tokenRequest),
  };

  let response;
  try {
    response = await fetch(
      grabIDClient.openIDConfiguration.tokenEndpoint,
      options,
    );
    response = await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('failed to POST token request', error);
  }
  const tokenResponse = TokenResponseWithRefreshToken.fromJSON(response);
  window.localStorage.setItem('grabid:access_token', tokenResponse.accessToken);
  window.localStorage.setItem('grabid:id_token', tokenResponse.idToken);
  window.localStorage.setItem(
    'grabid:refresh_token',
    tokenResponse.refreshToken,
  );
};

export const initGrabIDAuthClient = () => {
  const { redirectUri, clientId, openIdUrl } = getGrabIDConfigs();

  const scope = [
    'openid',
    'grab_express.partner_deliveries',
    'food.partner_api.express_deliveries',
    'geweb.pax',
    'profile.read',
    'geweb.pax_delivery',
  ].join(' ');

  const countryCode = window.localStorage.getItem(STORAGE_USER_COUNTRY_CODE);
  const acrValues = _isEmpty(countryCode)
    ? {
        service: 'PASSENGER',
        consentContext: {},
      }
    : {
        service: 'PASSENGER',
        consentContext: {
          countryCode,
        },
      };
  const ui_locales = countryCode
    ? COUNTRY_LOCALE_MAP[countryCode.toLowerCase()]
    : 'en' || 'en';

  const appConfig = {
    clientId,
    redirectUri,
    scope,
    acrValues,
    ui_locales,
  };

  const grabIDClient = new GrabID(openIdUrl, appConfig);

  return {
    getLoginReturnURI: () => GrabID.getLoginReturnURI(),

    startAuthRequest: async () => {
      await grabIDClient.getOpenIdConfiguration();
      grabIDClient.makeAuthorizationRequest();
    },

    parseAuthResponse: () => {
      GrabID.handleAuthorizationCodeFlowResponse();
    },

    getAuthToken: async () => {
      await grabIDClient.getOpenIdConfiguration();
      await makeTokenRequestHandleRefreshToken(grabIDClient);
      const result = GrabID.getResult();
      result.refreshToken = window.localStorage.getItem('grabid:refresh_token');
      saveAuthReponses(result);
      const { accessToken } = result;
      return accessToken;
    },
  };
};

export const grabIDLogout = async () => {
  const { openIdUrl } = getGrabIDConfigs();

  try {
    await fetch(`${openIdUrl}/logout`, {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
    });
  } catch (err) {
    const errorCtx = rumErrorCtx()
      .component('grabIDLogout')
      .message(err.message || 'Failed to logout from OpenID')
      .severity(SEVERITY.CRITICAL)
      .build();
    ddRum.addError(err, errorCtx);
    // eslint-disable-next-line no-console
    console.error(err);
  }

  Storage.removeItem('__AUTH_HINT__');
  clearAuthResponses();
  clearGrabIdTokens();
};
